import React, { useState, useMemo, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { comparePricesV2, offersAPI, ownProductsAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { ORDINARY_AVAILABILITY } from '../styles/layout';
import { PageHeader, Switch, Alert, Tile, FileButton, Icon, fmtNum } from '../ui';

const MIN_MARGIN = 0.07;   // мінімальна націнка від ціни входу
const RAISE_MIN = 0.01;    // рекомендуємо підняти, лише якщо ми дешевші за орієнтир більш ніж на 1%
const REFERENCE_SOURCE = 'infoshina';  // жорсткий орієнтир ціноутворення
const ROW_HEIGHT = 48;       // px per row (must match .ui-table--fixed row height)
const VIEWPORT_HEIGHT = 600; // visible table body height
const OVERSCAN = 8;          // extra rows above/below viewport

const PriceComparisonPage = () => {
    const [fileName, setFileName] = useState('');
    const [results, setResults] = useState([]);   // enriched rows (meta + rec baked in)
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all');
    const [inStockOnly, setInStockOnly] = useState(false);
    const [scrollTop, setScrollTop] = useState(0);
    const [sources, setSources] = useState([]);   // [{source_key, title}]

    // Колонки конкурентів будуються за списком джерел з бекенду, а не хардкодом —
    // додався новий парсер, з'явився і стовпчик.
    useEffect(() => {
        (async () => {
            try {
                const data = await offersAPI.sources();
                setSources((data.sources || []).filter(s => s.is_active));
            } catch (err) {
                console.error('Failed to load sources:', err);
            }
        })();
    }, []);

    // ПРАВИЛО: infoshina — жорсткий орієнтир. Дорожче за нього бути не можна.
    // Інші джерела — довідково, у правилі не беруть участі.
    // Пол: ціна не нижче за вхід × 1.07 (маржа 7%).
    // Позиція без ціни (немає в наявності) ринок не формує — рекомендації немає.
    const computeRecommendation = (file_price, competitors, cost) => {
        const ref = competitors[REFERENCE_SOURCE];
        const inf = ref && ref.price != null ? ref.price : null;
        if (inf == null || file_price == null) {
            const outOfStock = ref && ref.price == null && ref.in_stock === 0;
            return { type: 'none', label: outOfStock ? 'Орієнтир: немає' : '—', target: null };
        }
        const floor = cost != null ? cost * (1 + MIN_MARGIN) : null;

        if (file_price > inf) {
            // Ми дорожчі за орієнтир — так бути не повинно
            if (floor != null && inf < floor) {
                // знизитись до орієнтира не можна — маржа впаде нижче 7%
                return { type: 'blocked', label: 'Маржа < 7%', target: null };
            }
            return { type: 'lower', label: `Знизити до ${inf}`, target: inf };
        }
        if (file_price < inf * (1 - RAISE_MIN)) {
            // Ми дешевші — можна підняти до ціни орієнтира (рівна ціна допустима)
            return { type: 'raise', label: `Підняти до ${inf}`, target: inf };
        }
        return { type: 'market', label: 'В ринку', target: null };
    };

    const handleFile = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setFileName(file.name);
        setError(null);
        setResults([]);
        setStats(null);
        setScrollTop(0);
        setLoading(true);

        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            if (rows.length < 2) throw new Error('Файл порожній.');

            const header = rows[0].map(h => String(h).toLowerCase().trim());
            const idIdx = header.findIndex(h => h === 'id' || h === 'id товара' || h === 'my_id' || h.includes('id товара'));
            const priceIdx = header.findIndex(h => h.includes('цена продажи') || (h.includes('price') && !h.includes('out')) || h === 'цена' || h.includes('ціна продаж'));
            const nameIdx = header.findIndex(h => h.includes('название') || h.includes('назва') || h.includes('name'));
            let supplierIdx = header.findIndex(h => h.includes('поставщик') || h.includes('постачальник') || h.includes('supplier'));
            if (supplierIdx === -1) supplierIdx = 5;
            // «Цена входа» — у валюті постачальника (частина в USD), «…сконвертированная» — у гривнях.
            let costIdx = header.findIndex(h => h.includes('цена входа сконверт'));
            if (costIdx === -1) costIdx = header.findIndex(h => h.includes('цена входа') || h.includes('ціна входу') || h.includes('cost'));
            const qtyIdx = header.findIndex(h => h.includes('кол-во') || h.includes('кількість') || h.includes('qty') || h.includes('quantity'));
            const outIdx = header.findIndex(h => h.includes('цена выхода') || h.includes('ціна виходу') || h.includes('выход') || h.includes('виход'));
            if (idIdx === -1 || priceIdx === -1) throw new Error('Не знайдено колонок «Id товара» і «Цена продажи».');
            if (outIdx === -1) throw new Error('Не знайдено колонку "Цена выхода" — вона потрібна для визначення ціноутворюючої позиції.');

            const stripPrefix = (s) => String(s || '')
                .replace(/^(Летн(яя|ие)|Зимн(яя|ие)|Зимов[аі]|Літн[яі]|Всесезонн(ая|ые|а|і))\s+шин[аыиыні]*\s+/i, '')
                .trim();
            const num = (v) => {
                const n = parseFloat(String(v).replace(',', '.'));
                return isNaN(n) ? null : n;
            };

            const groups = {};
            for (let i = 1; i < rows.length; i++) {
                const row = rows[i];
                if (!row || row[idIdx] === undefined || row[idIdx] === '') continue;
                const id = parseInt(row[idIdx], 10);
                if (isNaN(id)) continue;
                const price = num(row[priceIdx]);
                const out = num(row[outIdx]);
                const cost = costIdx !== -1 ? num(row[costIdx]) : null;
                const qty = qtyIdx !== -1 ? (num(row[qtyIdx]) || 0) : 0;
                const supplier = supplierIdx !== -1 && row[supplierIdx] !== undefined ? String(row[supplierIdx]) : '';
                const name = nameIdx !== -1 ? stripPrefix(row[nameIdx]) : '';
                if (!groups[id]) groups[id] = { rows: [], name };
                groups[id].rows.push({ price, out, cost, qty, supplier });
            }

            const ids = Object.keys(groups).map(Number);
            if (ids.length === 0) throw new Error('У файлі немає рядків з ID товару.');

            const items = [];
            const metaMap = {};
            for (const id of ids) {
                const g = groups[id];
                const depth = g.rows.length;
                let ps = g.rows.find(r => r.price != null && r.out != null && r.price === r.out);
                if (!ps) ps = g.rows[0];
                const totalQty = g.rows.reduce((s, r) => s + (r.qty || 0), 0);
                const otherQty = totalQty - (ps.qty || 0);
                items.push({ id, price: ps.price });
                metaMap[id] = {
                    name: g.name, supplier: ps.supplier, cost: ps.cost,
                    qty: ps.qty || 0, otherQty, depth,
                };
            }

            const response = await comparePricesV2(items);
            const rawResults = response.results || [];

            // Enrich each result ONCE: bake in meta + margin + recommendation
            const enriched = rawResults.map(r => {
                const m = metaMap[r.my_id] || {};
                const margin = (m.cost != null && r.file_price != null) ? Math.round(r.file_price - m.cost) : null;
                const marginPct = (m.cost && r.file_price != null) ? Math.round((r.file_price - m.cost) / m.cost * 100) : null;
                const rec = computeRecommendation(r.file_price, r.competitors, m.cost);
                return { ...r, m, margin, marginPct, rec };
            });

            setResults(enriched);
            setStats({ total: response.total, matched: response.matched, no_mapping: response.no_mapping });

            // Каталог тепер зберігається в БД — щоб дашборд бачив історію наших цін,
            // а не лише зріз останнього завантаженого файлу.
            try {
                await ownProductsAPI.upload(items.map(it => ({
                    my_id: it.id,
                    name: metaMap[it.id]?.name || '',
                    cost: metaMap[it.id]?.cost ?? null,
                    price: it.price,
                    qty: metaMap[it.id]?.qty ?? null,
                })));
            } catch (err) {
                // Збереження каталогу не критичне — порівняння вже показано користувачу.
                console.error('Failed to persist own products:', err);
            }
        } catch (err) {
            console.error('Comparison error:', err);
            setError(err.message || 'Не вдалося обробити файл.');
        } finally {
            setLoading(false);
            e.target.value = '';
        }
    };

    const handleExport = () => {
        if (!results.length) return;
        const exportData = results.map(r => {
            const m = r.m || {};
            const row = {
                'My ID': r.my_id,
                'Товар': m.name || '',
                'Постачальник': m.supplier || '',
                'Глибина': m.depth ?? '',
                'Залишок': m.qty ?? '',
                'Залишок інших': m.otherQty ?? '',
                'Ціна входу': m.cost ?? '',
                'Маржа, грн': r.margin ?? '',
                'Маржа, %': r.marginPct ?? '',
                'My Price': r.file_price,
            };
            sources.forEach(s => {
                const c = r.competitors[s.source_key] || {};
                row[`${s.title} ID`] = c.competitor_id ?? '';
                row[`${s.title} Price`] = c.price ?? (c.in_stock === 0 ? 'немає' : '');
                row[`${s.title} Наявність`] = c.availability ?? '';
                row[`${s.title} Diff`] = c.diff ?? '';
                row[`${s.title} Diff %`] = c.diff_pct ?? '';
            });
            row['Рекомендація'] = r.rec.label;
            row['Цільова ціна'] = r.rec.target ?? '';
            return row;
        });
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Comparison');
        XLSX.writeFile(wb, `price_comparison_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const REC_CHIP = { raise: 'good', lower: 'bad', blocked: 'warn', market: '' };
    const diffColor = (diff) => {
        if (diff === null || diff === undefined) return 'var(--text-3)';
        if (diff < 0) return 'var(--good)';
        if (diff > 0) return 'var(--bad)';
        return 'var(--text-2)';
    };

    const recCounts = useMemo(() => {
        return results.reduce((acc, r) => {
            const t = r.rec.type;
            if (acc[t] !== undefined) acc[t]++;
            return acc;
        }, { raise: 0, lower: 0, market: 0, blocked: 0 });
    }, [results]);

    const filtered = useMemo(() => {
        return results.filter(r => {
            const m = r.m || {};
            if (inStockOnly && !((m.qty > 0) || (m.otherQty > 0))) return false;
            if (filter === 'all') return true;
            if (filter === 'matched') return r.matched_any;
            if (filter === 'no_mapping') return !r.matched_any;
            if (['raise', 'lower', 'market', 'blocked'].includes(filter)) return r.rec.type === filter;
            return true;
        });
    }, [results, filter, inStockOnly]);

    // Fixed column widths so header and virtualized body tables align
    const COLS = useMemo(() => [
        '100px', '300px', '130px', '80px', '100px', '90px', '130px', '120px',
        ...sources.flatMap(() => ['110px', '150px']),
        '160px',
    ], [sources]);
    // Обидві таблиці — точно на суму колонок: при width 100% тіло (зі смугою
    // прокрутки) виходило вужчим за шапку, і колонки роз'їжджались.
    const tableWidth = COLS.reduce((n, w) => n + parseInt(w, 10), 0);

    // Virtual scroll calculations
    const totalHeight = filtered.length * ROW_HEIGHT;
    const startIdx = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
    const endIdx = Math.min(filtered.length, Math.ceil((scrollTop + VIEWPORT_HEIGHT) / ROW_HEIGHT) + OVERSCAN);
    const visible = filtered.slice(startIdx, endIdx);
    const offsetY = startIdx * ROW_HEIGHT;

    const pickFilter = (key) => { setFilter(prev => (prev === key ? 'all' : key)); setScrollTop(0); };

    const renderCompetitor = (comp) => {
        if (!comp) {
            return (<>
                <td className="r sep muted">—</td>
                <td className="r muted">—</td>
            </>);
        }
        if (comp.price == null) {
            const oos = comp.in_stock === 0;
            return (<>
                <td className="r sep muted" style={{ fontSize: 12 }}>{oos ? 'немає' : '—'}</td>
                <td className="r muted">—</td>
            </>);
        }
        return (<>
            <td className="r sep" title={comp.availability && comp.availability !== ORDINARY_AVAILABILITY
                ? `Умова, за якої діє ця ціна у конкурента: ${comp.availability}` : undefined}>
                {fmtNum(comp.price)}
                {comp.availability && comp.availability !== ORDINARY_AVAILABILITY && (
                    <span style={{ color: 'var(--warn)', marginLeft: 4 }}>•</span>
                )}
            </td>
            <td className="r" style={{ color: diffColor(comp.diff), fontWeight: 600 }}>
                {comp.diff != null ? `${comp.diff > 0 ? '+' : ''}${comp.diff} (${comp.diff_pct > 0 ? '+' : ''}${comp.diff_pct}%)` : '—'}
            </td>
        </>);
    };

    const header = (
        <thead>
            <tr>
                <th>ID</th>
                <th>Товар</th>
                <th>Постач.</th>
                <th className="c">Глиб.</th>
                <th className="r">Залишок</th>
                <th className="r">Вхід</th>
                <th className="r">Маржа</th>
                <th className="r sep">Наша ціна</th>
                {sources.map(s => (
                    <React.Fragment key={s.source_key}>
                        <th className="r sep">{s.title}</th>
                        <th className="r">Різниця</th>
                    </React.Fragment>
                ))}
                <th className="sep">Рекомендація</th>
            </tr>
        </thead>
    );
    const colgroup = <colgroup>{COLS.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>;

    return (
        <div className="ui-page">
            <PageHeader
                title="Порівняння цін"
                description={<>
                    Завантаж свій прайс: кожен товар зіставляється через таблицю мапінгу й порівнюється
                    з усіма конкурентами. Орієнтир ціноутворення — {REFERENCE_SOURCE}.
                </>}
                actions={<>
                    {stats && (
                        <button className="ui-btn" onClick={handleExport}>
                            <Icon name="download" /> Excel
                        </button>
                    )}
                    <FileButton primary accept=".xlsx,.xls,.csv" onChange={handleFile} disabled={loading}>
                        {loading ? 'Порівнюю…' : 'Обрати Excel-файл'}
                    </FileButton>
                </>}
            />

            {!stats && !loading && !error && (
                <div className="ui-empty">
                    <p style={{ margin: 0, color: 'var(--text)', fontWeight: 600 }}>Файл ще не завантажено</p>
                    <p style={{ margin: '8px 0 0', fontSize: 14 }}>
                        Потрібні колонки «Id товара», «Цена продажи» і «Цена выхода»; «Цена входа», «Кол-во»
                        й «Поставщик» — для маржі та залишків.
                    </p>
                </div>
            )}
            {fileName && <div className="muted" style={{ fontSize: 13 }}>Файл: {fileName}</div>}

            {loading && <LoadingSpinner />}
            {error && <Alert kind="error">{error}</Alert>}

            {stats && (
                <>
                    <section className="ui-tiles" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
                        <Tile label="Всього" value={fmtNum(stats.total)} active={filter === 'all'} onClick={() => pickFilter('all')} />
                        <Tile label="Зіставлено" value={fmtNum(stats.matched)} chipKind="good"
                              chip={stats.total ? `${Math.round(stats.matched / stats.total * 100)}%` : null}
                              active={filter === 'matched'} onClick={() => pickFilter('matched')} />
                        <Tile label="Можна підняти" value={fmtNum(recCounts.raise)} chip="↑" chipKind="good"
                              active={filter === 'raise'} onClick={() => pickFilter('raise')} />
                        <Tile label="Дорожчі за орієнтир" value={fmtNum(recCounts.lower)} chip="↓" chipKind="bad"
                              active={filter === 'lower'} onClick={() => pickFilter('lower')} />
                        <Tile label={'Маржа < 7%'} value={fmtNum(recCounts.blocked)} chip="!" chipKind="warn"
                              active={filter === 'blocked'} onClick={() => pickFilter('blocked')} />
                        <Tile label="В ринку" value={fmtNum(recCounts.market)}
                              active={filter === 'market'} onClick={() => pickFilter('market')} />
                    </section>

                    <section className="ui-card ui-card--flush">
                        <div className="ui-card-head">
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                                <h2 className="ui-section-title" style={{ fontSize: 20 }}>Результат</h2>
                                <span className="muted num" style={{ fontSize: 14 }}>{fmtNum(filtered.length)} рядків</span>
                            </div>
                            <div className="ui-toolbar">
                                <button className={`ui-pill${filter === 'no_mapping' ? ' is-on' : ''}`} onClick={() => pickFilter('no_mapping')}>
                                    Без зіставлення <span className="ui-pill-count">{fmtNum(stats.no_mapping)}</span>
                                </button>
                                <Switch checked={inStockOnly} onChange={v => { setInStockOnly(v); setScrollTop(0); }}>
                                    Тільки в наявності
                                </Switch>
                            </div>
                        </div>

                        <div style={{ overflowX: 'auto', marginTop: 8 }}>
                            {/* Запас під смугу прокрутки тіла, щоб остання колонка не обрізалась. */}
                            <div style={{ minWidth: tableWidth + 16 }}>
                                {/* Header table (fixed) */}
                                <table className="ui-table ui-table--fixed" style={{ tableLayout: 'fixed', width: tableWidth }}>
                                    {colgroup}
                                    {header}
                                </table>

                                {/* Virtualized body */}
                                <div
                                    onScroll={e => setScrollTop(e.currentTarget.scrollTop)}
                                    style={{ height: VIEWPORT_HEIGHT, overflowY: 'auto', overflowX: 'hidden' }}
                                >
                                    <div style={{ height: totalHeight, position: 'relative' }}>
                                        <table className="ui-table ui-table--fixed"
                                               style={{ tableLayout: 'fixed', width: tableWidth, position: 'absolute', top: offsetY, left: 0 }}>
                                            {colgroup}
                                            <tbody>
                                                {visible.map((r, i) => {
                                                    const m = r.m || {};
                                                    const chip = REC_CHIP[r.rec.type];
                                                    return (
                                                        <tr key={startIdx + i} style={{ height: ROW_HEIGHT, background: r.matched_any ? undefined : 'var(--warn-soft)' }}>
                                                            <td className="muted-2">{r.my_id}</td>
                                                            <td title={m.name || ''} style={{ fontWeight: 600 }}>{m.name || '—'}</td>
                                                            <td className="muted-2" style={{ fontSize: 12 }} title={m.supplier || ''}>{m.supplier || '—'}</td>
                                                            <td className="c">{m.depth || '—'}</td>
                                                            <td className="r" style={{ color: m.qty > 0 ? undefined : 'var(--bad)' }}>
                                                                {m.qty ?? '—'}
                                                                {!(m.qty > 0) && m.otherQty > 0 && (
                                                                    <span title="Ціноутворюючої позиції немає на складі, але є інші постачальники"
                                                                          style={{ color: 'var(--warn)', fontSize: 11, marginLeft: 4 }}>(+{m.otherQty})</span>
                                                                )}
                                                            </td>
                                                            <td className="r muted-2">{fmtNum(m.cost)}</td>
                                                            <td className="r" style={{ color: r.margin != null ? (r.margin > 0 ? undefined : 'var(--bad)') : 'var(--text-3)' }}>
                                                                {r.margin != null ? `${fmtNum(r.margin)}${r.marginPct != null ? ` (${r.marginPct}%)` : ''}` : '—'}
                                                            </td>
                                                            <td className="r sep" style={{ fontWeight: 700 }}>{fmtNum(r.file_price)}</td>
                                                            {sources.map(s => (
                                                                <React.Fragment key={s.source_key}>
                                                                    {renderCompetitor(r.competitors[s.source_key])}
                                                                </React.Fragment>
                                                            ))}
                                                            <td className="sep">
                                                                {r.rec.type === 'none' ? (
                                                                    <span className="muted" style={{ fontSize: 12 }}>{r.rec.label}</span>
                                                                ) : (
                                                                    <span className={`ui-chip${chip ? ` ui-chip--${chip}` : ''}`}>{r.rec.label}</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </>
            )}
        </div>
    );
};

export default PriceComparisonPage;
